import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { defer, filter, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { Store } from '@ngrx/store';

import { AuthService } from '../auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { CurrentUser } from '../auth/current-user';
import { Customer, User, UsersApi } from '@api/index';
import { UserLoad, UserLoadProfileSuccess } from '@store/user';
import { selectUserCustomers } from '@store/user/user.selectors';
import { UserProfileResponse } from '@api/models/user-login-response';
import { AppService } from '@app/app.service';
import { SIDENAV_MENU } from './sidenav.config';
import { AppState } from '@store/store.config';
import { SystemMessageService } from '../services/system-message.service';
import { TranslateConfigService } from '../translate-config.service';

@Component({
  selector: 'flow-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() currentUser!: CurrentUser;

  private destroy$ = new Subject<void>();
  customers: Customer[];
  selectedCustomer: Customer;
  isAdmin: boolean;
  initials = '';
  showNavSettings = false;
  adminUser = { id: -1, name: 'Admin' };
  SIDENAV_MENU = SIDENAV_MENU;

  constructor(
    private authService: AuthService,
    private router: Router,
    private usersApi: UsersApi,
    private store: Store<AppState>,
    public appService: AppService,
    private systemMessageService: SystemMessageService,
    private translateConfigService: TranslateConfigService
  ) { }

  ngOnInit(): void {
    this.currentUser = this.authService.getUser();
    this.isAdmin = this.currentUser?.roleId === UserRoleEnum.Admin;
    this.initials = this.getInitials(this.currentUser?.fullName);

    this.getCustomers(this.currentUser);
  }

  getCustomers(user: User): void {
    if (this.isAdmin) {
      this.store.dispatch(UserLoad({}));
    } else {
      this.store.dispatch(UserLoad({ userId: user.id }));
    }

    this.store
      .select(selectUserCustomers)
      .pipe(
        takeUntil(this.destroy$),
        filter(customers => !!customers && customers.length > 0),
        take(1)
      )
      .subscribe(customers => {
        this.customers = this.isAdmin ? [this.adminUser, ...customers] : [...customers];
        this.setSelectedCustomer(this.currentUser);
      });
  }

  onCustomerChange(newCustomerId: number) {
    defer(() => {
      return this.isAdmin
        ? of(this.authService.user)
        : this.usersApi.selectProfile(newCustomerId).pipe(
          tap((customer: any) => {
            this.authService.updateUserInfo(customer.email, customer.name);
            this.authService.processToken({ token: customer.token, refreshToken: customer.refreshToken });
            this.authService.setLanguage(customer.language);
          })
        );
    })
      .pipe(switchMap((customer: any) => this.setCustomer(newCustomerId, customer)))
      .subscribe({
        next: (currentUser: CurrentUser) => {
          this.setSelectedCustomer(currentUser);
          this.router.navigate(['/home']).then(() => window.location.reload());
        },
        error: () => {
          this.systemMessageService.error(this.translateConfigService.instantWithParams('notifications.error.genericError'));
        },
      });
  }

  setCustomer(newCustomerId: number, customer: UserProfileResponse) {
    const currentUser = this.authService.setCustomer(customer, newCustomerId);
    return of(currentUser);
  }

  setSelectedCustomer(currentUser) {
    const id = currentUser.currentCustomerId || currentUser.customerId;
    const customer = this.customers?.find(c => c.id === id) || this.adminUser;
    this.selectedCustomer = customer;
    this.store.dispatch(UserLoadProfileSuccess({ response: { ...currentUser } }));
  }

  getInitials(name) {
    return name
      ?.split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }

  showSettings(e) {
    e.stopPropagation();
    e.preventDefault();
    this.showNavSettings = true;
  }

  closeSettings(e) {
    this.showNavSettings = false;
  }

  redirectToHomePage() {
    this.router.navigate(['/home']);
  }

  toggleSidenav() {
    this.appService.navClosed = !this.appService.navClosed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
